import React from "react";
import { motion } from "framer-motion";
import { Stethoscope, Target, Ban, Clock, Compass, ShieldAlert } from "lucide-react";

const CONFIDENCE = {
  High: { color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  Medium: { color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  Low: { color: "#F43F5E", bg: "rgba(244,63,94,0.12)" },
};

function Section({ icon: Icon, label, children, accent, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 * index, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="border-t border-white/8 py-5 first:border-t-0 first:pt-0"
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md" style={{ background: accent + "1F", color: accent }}>
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <span className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>{label}</span>
      </div>
      <div className="mt-3 pl-[38px] text-[15px] leading-relaxed text-neutral-200">{children}</div>
    </motion.div>
  );
}

export default function YourMinute({ category, recommendation: r }) {
  if (!r) return null;
  const conf = CONFIDENCE[r.confidence] || CONFIDENCE.Medium;
  return (
    <div data-testid="your-minute" className="relative overflow-hidden rounded-2xl border border-white/10 bg-[var(--surface)] p-6 sm:p-9">
      <div className="mb-6 flex flex-col gap-3 border-b border-white/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <span className="eyebrow">Decision compiled</span>
          <h2 className="font-head mt-1 text-3xl font-black tracking-tight text-white sm:text-4xl">YOUR MINUTE</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="ym-category" className="rounded-full border border-white/12 px-3 py-1.5 font-mono2 text-[11px] uppercase tracking-wider text-neutral-300">
            {category}
          </span>
          <span data-testid="ym-confidence" className="rounded-full px-3 py-1.5 font-mono2 text-[11px] font-semibold uppercase tracking-wider" style={{ background: conf.bg, color: conf.color }}>
            Confidence: {r.confidence}
          </span>
        </div>
      </div>

      <Section index={0} icon={Stethoscope} label="The Diagnosis" accent="#A0A6B2">
        <span data-testid="ym-diagnosis">{r.diagnosis}</span>
      </Section>
      <Section index={1} icon={Target} label="Do This" accent="#FF6B35">
        <span data-testid="ym-dothis" className="font-medium text-white">{r.do_this}</span>
      </Section>
      <Section index={2} icon={Ban} label="Avoid This" accent="#F43F5E">
        <span data-testid="ym-avoid">{r.avoid_this}</span>
      </Section>
      <Section index={3} icon={Clock} label="Your Next 15 Minutes" accent="#10B981">
        <span data-testid="ym-next15">{r.next_15_minutes}</span>
      </Section>
      <Section index={4} icon={Compass} label="Why" accent="#A0A6B2">
        <span data-testid="ym-why">{r.why}</span>
      </Section>

      {r.key_assumption ? (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="mt-6 flex items-start gap-2.5 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--caution)]" />
          <p className="text-sm text-neutral-300">
            <span className="font-mono2 text-[11px] uppercase tracking-wider text-[var(--caution)]">Key assumption · </span>
            <span data-testid="ym-assumption">{r.key_assumption}</span>
          </p>
        </motion.div>
      ) : null}
    </div>
  );
}
